import React, { useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Logger } from '@saviynt/common';
import {
  Box,
  Button,
  InputField,
  Loader,
  MenuInput,
  type MenuOption,
  RadioButton,
  Upload,
  useUploadFile,
} from '@saviynt/design-system';

import {
  ALERT_BANNER_DEFAULT,
  COPILOT_WORKFLOWS,
  DEFAULT_SEARCH_DEBOUNCE,
  GLOBAL_ALERT_BANNER_TYPES,
} from '../../constants';
import { useEffectAfterMount } from '../../hooks';
import { type EndpointBlock } from '../../types';

import { FORM_IP_MAP } from './constants';
import {
  useFileValidation,
  // useFileValidation,
  useFormContext,
  useGetSelectOptions,
} from './hooks';
import {
  type File as CustomFile,
  type InputValueType,
  type TrackedFormData,
} from './types';
import { blobToBase64, updateEndpointData, updateForm } from './utils';

interface FormInputElementProps {
  name: string;
  type: string;
  value?: InputValueType;
  initialOptions?: MenuOption[];
  placeholder?: string;
  endpoint?: EndpointBlock;
  isCritical: boolean;
  isEditable?: boolean;
  isMultiple?: boolean;
  totalFileSize?: number;
  fileCount?: number;
  fileSize?: number;
  formats?: string[];
  setFormData: React.Dispatch<React.SetStateAction<TrackedFormData[]>>;
}

/**
 * Renders a form input element based on the given field type. Supports
 * text areas, single-line inputs, radio buttons, select menus, file
 * uploads, and stub cases for checkboxes and dates.
 *
 * @param props - {@link FormInputElementProps}
 * @returns The appropriate input element for the field type, or `null`
 *   for unsupported types.
 */
function FormInputElement({
  name,
  type,
  value,
  initialOptions,
  placeholder,
  isEditable,
  setFormData,
  endpoint,
  isCritical,
  isMultiple,
  totalFileSize,
  fileCount,
  fileSize,
  formats,
}: FormInputElementProps) {
  const { setAlertBanner, workflowType } = useFormContext();

  // Input states
  const [textAreaInput, setTextAreaInput] = useState(value ?? '');
  const [normalInput, setNormalInput] = useState(value ?? '');
  const [options, setOptions] = useState(initialOptions ?? []);
  const [radioValue, setRadioValue] = useState(
    initialOptions?.find((o) => o.isChecked)?.value ?? ''
  );
  const {
    uploadedFiles,
    handleFileSelect,
    handleFileDelete,
    isMaxNumberUploaded,
    isMaxTotalSizeReached,
    maxFileCount,
    maxFileSize,
    maxTotalFileSize,
    hasAnyUploadErrors,
  } = useUploadFile(
    totalFileSize ?? 2,
    fileCount ?? 10,
    fileSize ?? 2,
    false,
    false
  );

  const { handlePostList, isPostLoading } = useGetSelectOptions();
  const { validateFiles, isUploading } = useFileValidation();
  const [isValidatingFile, setIsValidatingFile] = useState(false);
  // const [validatedFiles, setValidatedFiles] = useState<Set<string>>(new Set());

  // DONE: 'textarea' => value
  // DONE: 'radio' => options
  // DONE: 'select' => options
  // DONE: 'input' => value
  // DONE: 'upload; => file
  // | 'checkbox'  => options
  // | 'date';  => value

  // Handling logic
  // Debounced effect for text area
  useEffectAfterMount(() => {
    if (type === FORM_IP_MAP.TEXTAREA) {
      const handler = setTimeout(() => {
        setFormData((prevFormData) =>
          updateForm(name, type, prevFormData, { value: textAreaInput })
        );
      }, DEFAULT_SEARCH_DEBOUNCE);

      return () => {
        clearTimeout(handler);
      };
    }

    return undefined;
    // Should only depend on `textAreaInput`
  }, [textAreaInput]);

  // Debounced effect for input
  useEffectAfterMount(() => {
    if (type === FORM_IP_MAP.INPUT) {
      const handler = setTimeout(() => {
        setFormData((prevFormData) =>
          updateForm(name, type, prevFormData, { value: normalInput })
        );
      }, DEFAULT_SEARCH_DEBOUNCE);

      return () => {
        clearTimeout(handler);
      };
    }

    return undefined;
    // Should only depend on `normalInput`
  }, [normalInput]);

  // Radio logic
  const handleRadioClick = (_: React.MouseEvent, rv: string) => {
    const modifiedOptions = (initialOptions ?? []).map((o: MenuOption) => ({
      ...o,
      isChecked: o.value === rv,
    }));

    setRadioValue(rv);
    setFormData((prevFormData) =>
      updateForm(name, type, prevFormData, { options: modifiedOptions })
    );
  };

  // Select logic
  const handleSelectChange = (
    selectedOption: MenuOption | undefined | null
  ) => {
    const modifiedOptions = options.map((option) => ({
      ...option,
      isChecked: !selectedOption
        ? false
        : option.value === selectedOption.value,
    }));

    setOptions(() => modifiedOptions);
    setFormData((prevFormData) => {
      const tempFormData = updateForm(name, type, prevFormData, {
        options: modifiedOptions,
      });

      if (name === 'acctype') {
        const epUpdatedFormData = updateEndpointData(
          name,
          modifiedOptions.find((o) => o.isChecked)?.value ?? '',
          tempFormData
        );

        return epUpdatedFormData;
      }

      return tempFormData;
    });
  };

  // Upload logic
  useEffectAfterMount(() => {
    if (uploadedFiles.length > 0) {
      const blobPromise = Promise.all(
        uploadedFiles.map((f) => blobToBase64(f.file))
      );

      blobPromise
        .then((response) => {
          setFormData((prevFormData) =>
            updateForm(name, type, prevFormData, {
              files: response.map((v, idx) => ({
                fileName: uploadedFiles[idx].fileName,
                fileType: uploadedFiles[idx].file.type,
                fileSize: uploadedFiles[idx].fileSize,
                base64String: v,
              })) as CustomFile[],
            })
          );
        })
        .catch(() => {
          Logger.log('Failed to convert the file to base 64 string');
        });
    }
    // should depend only on `uploadedFiles`
  }, [uploadedFiles]);

  useEffect(() => {
    if (hasAnyUploadErrors) {
      setAlertBanner({
        ...ALERT_BANNER_DEFAULT,
        kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
        title: 'Failed',
        description: 'Not able to capture file, please try again',
        isOpen: true,
      });
    }
  }, [hasAnyUploadErrors, setAlertBanner]);

  // Input render
  if (type === FORM_IP_MAP.TEXTAREA) {
    return (
      <InputField
        name='basic'
        placeholder={placeholder ?? ''}
        value={textAreaInput}
        setValue={setTextAreaInput}
        kind='multiline'
        size='large'
        isCritical={isCritical}
        isDisabled={!isEditable}
        regexPattern='<\/?[a-z][\s\S]*?>'
      />
    );
  }

  if (type === FORM_IP_MAP.INPUT) {
    return (
      <>
        <InputField
          name='basic'
          placeholder={placeholder ?? ''}
          value={normalInput}
          setValue={setNormalInput}
          kind='singleline'
          size='large'
          isCritical={isCritical}
          isDisabled={!isEditable}
          regexPattern='<\/?[a-z][\s\S]*?>'
          className={
            name === 'accountName'
              ? 'FormInputElement-singleLineInput--isAccountName'
              : ''
          }
        />
        {workflowType &&
          workflowType === COPILOT_WORKFLOWS.RQ_SA_CREATE.key &&
          name === 'accountName' &&
          endpoint && (
            <Button
              size='small'
              kind='subtle'
              isLoading={isPostLoading}
              isDisabled={isPostLoading}
              loaderText='Generating...'
              onClick={() => {
                handlePostList({
                  url: endpoint.url,
                  method: endpoint.method,
                  payload: endpoint.payload,
                })
                  .then((response) => {
                    const typedResponse = response as {
                      data: {
                        accountName: string;
                        hasNamingRule: boolean;
                      };
                      msg: string;
                    };

                    if (typedResponse.msg !== 'success') {
                      throw Error('Error generating name');
                    }

                    if (!typedResponse.data.hasNamingRule) {
                      setAlertBanner({
                        ...ALERT_BANNER_DEFAULT,
                        kind: GLOBAL_ALERT_BANNER_TYPES.INFO,
                        title: 'Naming rule',
                        description: 'No naming rule has been defined',
                        isOpen: true,
                      });

                      return;
                    }

                    if (!typedResponse.data.accountName) {
                      setAlertBanner({
                        ...ALERT_BANNER_DEFAULT,
                        kind: GLOBAL_ALERT_BANNER_TYPES.INFO,
                        title: 'Naming rule',
                        description:
                          'Account name is not generated. Please verify rule and dynamic attributes or enter name manually.',
                        isOpen: true,
                      });

                      return;
                    }

                    setNormalInput(typedResponse.data.accountName);
                  })
                  .catch(() => {
                    setAlertBanner({
                      ...ALERT_BANNER_DEFAULT,
                      kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                      title: 'Failed',
                      description:
                        'Not able to generate the account name, please try again later.',
                      isOpen: true,
                    });
                  });
              }}
              className='FormInputElement-singleLineInputBtn--isAccountName'>
              <FormattedMessage
                id='RRK8SA'
                defaultMessage='Generate'
                description='Button text for generating account name based on naming rules'
              />
            </Button>
          )}
      </>
    );
  }

  if (type === FORM_IP_MAP.RADIO) {
    return (
      <Box tag='div' className='FormInputElement-radioBox'>
        {initialOptions?.map((option) => (
          <RadioButton
            key={option.value}
            id={option.value}
            size='large'
            name={name}
            title={option.label}
            value={option.value}
            titleLineLimit={1}
            radioSelection={radioValue}
            onClick={handleRadioClick}
            isDisabled={!isEditable}
            className='FormInputElement-radioBoxItem'
          />
        ))}
      </Box>
    );
  }

  if (type === FORM_IP_MAP.SELECT) {
    return (
      <MenuInput
        inputSize='large'
        placeholder={placeholder ?? ''}
        options={options}
        onChange={handleSelectChange}
        loadingText='Loading...'
        isPortalEnabled
        isCritical={isCritical}
        isDisabled={!isEditable}
      />
    );
  }

  if (type === FORM_IP_MAP.UPLOAD) {
    const allowedFormats =
      formats && Array.isArray(formats)
        ? formats.map((f) => `.${f}`).join(',')
        : '.xls,.xlsx,.csv,.txt,.doc,.docx,.pdf,.msg,.ppt,.pptx,.rtf,.png,.jpeg,.jpg,.xml,.json';

    return (
      <Box tag='div' className='FormInputElement-uploadWrapper'>
        {isValidatingFile ? (
          <Loader kind='dots' format='inline' color='brand' />
        ) : (
          <Upload
            kind='dropzone'
            isMultiple={Boolean(isMultiple)}
            isUploading={isUploading}
            onFileSelect={(
              files: File[],
              allowedFileTypes?: string[],
              fileBeingReplaced?: string
            ) => {
              const prevNameSet = new Set(uploadedFiles.map((f) => f.fileName));
              const currentNameSet = new Set(files.map((f) => f.name));
              const intersectionSet = [...prevNameSet].filter((f) =>
                currentNameSet.has(f)
              );

              if (intersectionSet.length > 0) {
                setAlertBanner({
                  ...ALERT_BANNER_DEFAULT,
                  kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                  title: 'Failed',
                  description: `File with name ${intersectionSet[0]} already added`,
                  isOpen: true,
                });

                return;
              }

              setIsValidatingFile(true);

              validateFiles(files)
                .then(({ failedFiles, successMessage }) => {
                  const failedFileNames = failedFiles.map((f) => f.name);
                  const verifiedFiles = files.filter(
                    (f) => !failedFileNames.includes(f.name)
                  );

                  handleFileSelect(
                    verifiedFiles,
                    allowedFileTypes,
                    fileBeingReplaced
                  ).catch((err) => {
                    Logger.log('Error uploading files', err);

                    setAlertBanner({
                      ...ALERT_BANNER_DEFAULT,
                      kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                      title: 'Failed',
                      description:
                        'Not able to upload the files, please try again',
                      isOpen: true,
                    });
                  });

                  if (failedFiles.length > 0) {
                    const errorMessage = failedFiles[0].error;

                    setAlertBanner({
                      ...ALERT_BANNER_DEFAULT,
                      kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                      title: 'Failed',
                      description: errorMessage,
                      isOpen: true,
                    });
                  }

                  if (verifiedFiles.length > 0 && failedFiles.length === 0) {
                    setAlertBanner({
                      ...ALERT_BANNER_DEFAULT,
                      kind: GLOBAL_ALERT_BANNER_TYPES.SUCCESS,
                      title: 'Success',
                      description: successMessage,
                      isOpen: true,
                    });
                  }

                  setIsValidatingFile(false);
                })
                .catch(() => {
                  setAlertBanner({
                    ...ALERT_BANNER_DEFAULT,
                    kind: GLOBAL_ALERT_BANNER_TYPES.CRITICAL,
                    title: 'Failed',
                    description:
                      'Not able to upload the files, please try again',
                    isOpen: true,
                  });

                  setIsValidatingFile(false);
                });
            }}
            onFileDelete={handleFileDelete}
            uploadedFiles={uploadedFiles}
            acceptTypes={allowedFormats}
            isMaxNumberUploaded={isMaxNumberUploaded}
            isMaxTotalSizeReached={isMaxTotalSizeReached}
            maxFileCount={maxFileCount}
            maxFileSize={maxFileSize}
            maxTotalFileSize={maxTotalFileSize}
          />
        )}
      </Box>
    );
  }

  if (type === FORM_IP_MAP.CHECKBOX || type === FORM_IP_MAP.DATE) {
    return null;
  }

  return null;
}

FormInputElement.defaultProps = {
  placeholder: '',
  value: null,
  initialOptions: null,
  endpoint: undefined,
  isEditable: true,
  isMultiple: false,
  totalFileSize: 2,
  fileCount: 10,
  fileSize: 2,
  formats: null,
};

export default FormInputElement;
